import { Clock } from "lucide-react";
import { useMemo } from "react";
import { useActiveUserWorkItems } from "../../../hooks/useActiveUserWorkItems";
import {
  WorkItemStateEnum,
  WorkItemTypeEnum,
} from "../../../store/services/timelineApiRTK";
import StatCard from "../../ui/cards/StatCard";

const typeOfActivity = (type: WorkItemTypeEnum): string => {
  switch (type) {
    case WorkItemTypeEnum.PhoneCall:
    case WorkItemTypeEnum.FollowUpPhoneCall:
      return "Samtal";
    case WorkItemTypeEnum.Email:
    case WorkItemTypeEnum.FollowUpEmail:
      return "E-post";
    case WorkItemTypeEnum.PhysicalMeeting:
    case WorkItemTypeEnum.FollowUpPhysicalMeeting:
      return "Möte";
    case WorkItemTypeEnum.DigitalMeeting:
    case WorkItemTypeEnum.FollowUpDigitalMeeting:
      return "Digitalt möte";
    case WorkItemTypeEnum.Sms:
    case WorkItemTypeEnum.FollowUpSms:
      return "SMS";
    case WorkItemTypeEnum.OpportunityCall:
      return "Säljsamtal";
    case WorkItemTypeEnum.WelcomeCall:
      return "Välkomstsamtal";
    default:
      return "Övrigt";
  }
};

const UpcomingActivities = () => {
  const { data: workItems = [], isFetching } = useActiveUserWorkItems({
    state: WorkItemStateEnum.Active,
  });
  const upcomingActivities = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    return workItems.filter((item) => {
      // Bara aktiva aktiviteter
      if (item.State !== WorkItemStateEnum.Active) return false;

      if (!item.ScheduledTo) return false;

      const scheduledDate = new Date(item.ScheduledTo);
      if (isNaN(scheduledDate.getTime())) return false;

      // Aktiviteter från idag och framåt
      return scheduledDate >= startOfToday;
    });
  }, [workItems]);

  const { todayCount, upcomingCount, activityCounts } = useMemo(() => {
    const now = new Date();
    const today = now.toDateString();

    let todayActivities = 0;
    let futureActivities = 0;
    const counts: Record<string, number> = {};

    upcomingActivities.forEach((item) => {
      const scheduledDate = new Date(item.ScheduledTo);
      const type = typeOfActivity(item.Type);

      // Räkna typer
      counts[type] = (counts[type] || 0) + 1;

      // Räkna idag vs framtida
      if (scheduledDate.toDateString() === today) {
        todayActivities++;
      } else {
        futureActivities++;
      }
    });

    return {
      todayCount: todayActivities,
      upcomingCount: futureActivities,
      activityCounts: counts,
    };
  }, [upcomingActivities]);

  const getTitle = (): string => {
    const total = upcomingActivities.length;

    if (total === 0) {
      return "Inga kommande aktiviteter";
    }

    if (total === 1) {
      return "1 kommande aktivitet";
    }

    return `${total} kommande aktiviteter`;
  };

  const getSubtitle = (): string => {
    if (upcomingActivities.length === 0) {
      return "Allt är klart för tillfället";
    }

    const parts: string[] = [];

    // Lägg till dagens aktiviteter om det finns några
    if (todayCount > 0) {
      parts.push(`${todayCount} idag`);
    }

    // Lägg till framtida aktiviteter om det finns några
    if (upcomingCount > 0) {
      parts.push(`${upcomingCount} senare`);
    }

    // Om vi bara har aktiviteter för idag, visa typfördelning istället
    if (upcomingCount === 0 && todayCount > 0) {
      const activityText = Object.entries(activityCounts)
        .filter(([, count]) => count > 0)
        .slice(0, 2) // Max 2 typer för att hålla texten kort
        .map(([type, count]) => `${count} ${type.toLowerCase()}`)
        .join(", ");

      return activityText;
    }

    return parts.join(" • ");
  };

  const getTimeframe = (): string => {
    if (upcomingActivities.length === 0) return "";

    // Hitta närmaste och senaste aktivitet
    const dates = upcomingActivities
      .map((item) => new Date(item.ScheduledTo))
      .sort((a, b) => a.getTime() - b.getTime());

    const earliest = dates[0];
    const latest = dates[dates.length - 1];
    const now = new Date();

    // Om allt är idag
    if (
      earliest.toDateString() === now.toDateString() &&
      latest.toDateString() === now.toDateString()
    ) {
      return "idag";
    }

    // Om närmaste är idag
    if (earliest.toDateString() === now.toDateString()) {
      const daysUntilLast = Math.ceil(
        (latest.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
      return `idag - ${daysUntilLast} dagar`;
    }

    // Annars visa när det börjar
    const daysUntilFirst = Math.ceil(
      (earliest.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysUntilFirst === 1) {
      return "från imorgon";
    } else if (daysUntilFirst <= 7) {
      return `från ${daysUntilFirst} dagar`;
    } else {
      return "kommande veckor";
    }
  };

  // Bestäm färg baserat på antal aktiviteter
  const getColor = (): string => {
    const total = upcomingActivities.length;
    if (total === 0) return "#10b981"; // Grön - bra läge
    if (total <= 3) return "#3b82f6"; // Blå - lagom
    if (total <= 6) return "#f59e0b"; // Gul - lite mycket
    return "#ef4444"; // Röd - mycket att göra
  };

  return (
    <StatCard
      icon={<Clock />}
      title={getTitle()}
      color={getColor()}
      description={getSubtitle()}
      subtitle={getTimeframe()}
      to="/activities"
      loading={isFetching}
    />
  );
};

export default UpcomingActivities;
